import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import * as d3 from 'd3'
import { parseDay, teamChange } from './data'
import { useElementWidth } from './useElementSize'
import { RankDelta, TeamLogo, describeEvent, formatDay, formatScore } from './ui'

const HEIGHT = 540
const MARGIN = { top: 34, right: 52, bottom: 30, left: 40 }
const tickFormat = d3.utcFormat('%b %-d')

export default function LeagueChart({ league, dates, metric, focusId, hoverId, onHover, pinnedDate, onPick }) {
  const [ref, width] = useElementWidth()
  const [hover, setHover] = useState(null) // { date, x, y }

  const innerW = Math.max(0, width - MARGIN.left - MARGIN.right)
  const innerH = HEIGHT - MARGIN.top - MARGIN.bottom
  const startIdx = league.dateIndex.get(dates[0]) ?? 0
  const endIdx = league.dateIndex.get(dates.at(-1)) ?? 0

  const { x, y, paths, yTicks } = useMemo(() => {
    const x = d3.scaleUtc().domain(d3.extent(dates, parseDay)).range([0, innerW])
    let y
    let yTicks
    if (metric === 'rank') {
      y = d3.scaleLinear().domain([0.5, league.teams.length + 0.5]).range([0, innerH])
      yTicks = [1, 5, 10, 15, 20, 25, 30].filter(t => t <= league.teams.length)
    } else {
      const values = league.teams.flatMap(t => t.series.slice(startIdx, endIdx + 1).filter(Boolean).map(v => v.score))
      y = d3.scaleLinear().domain(d3.extent(values)).nice().range([innerH, 0])
      yTicks = y.ticks(6)
    }
    const line = d3
      .line()
      .defined(d => d)
      .x(d => x(parseDay(d.date)))
      .y(d => y(d[metric]))
      .curve(d3.curveMonotoneX)
    const paths = new Map(league.teams.map(t => [t.id, line(t.series.slice(startIdx, endIdx + 1)) || '']))
    return { x, y, paths, yTicks }
  }, [league, dates, metric, innerW, innerH, startIdx, endIdx])

  const xs = useMemo(() => dates.map(d => x(parseDay(d))), [dates, x])

  const handleMove = e => {
    const rect = e.currentTarget.getBoundingClientRect()
    const px = e.clientX - rect.left
    const py = e.clientY - rect.top
    const i = Math.max(0, Math.min(dates.length - 1, d3.bisectCenter(xs, px)))
    const date = dates[i]
    let nearest = null
    let best = Infinity
    for (const t of league.teams) {
      const v = t.series[league.dateIndex.get(date)]
      if (!v) continue
      const dist = Math.abs(y(v[metric]) - py)
      if (dist < best) {
        best = dist
        nearest = t
      }
    }
    setHover({ date, x: xs[i], y: py })
    onHover(nearest && best < 24 ? nearest.id : null)
  }

  const handleLeave = () => {
    setHover(null)
    onHover(null)
  }

  const handleClick = () => {
    if (hover) onPick({ teamId: hoverId, date: hover.date })
  }

  const emphasized = hoverId || focusId
  const order = [...league.teams].sort((a, b) => (a.id === focusId) - (b.id === focusId) || (a.id === hoverId) - (b.id === hoverId))
  const focus = focusId ? league.byId.get(focusId) : null
  const hovered = hoverId ? league.byId.get(hoverId) : null
  const lastDate = dates.at(-1)

  const tooltipTeam = hovered
  const tooltipChange = tooltipTeam && hover ? teamChange(league, tooltipTeam, hover.date) : null
  const tooltipValue = tooltipTeam && hover ? tooltipTeam.series[league.dateIndex.get(hover.date)] : null

  return (
    <div className="hv-chart" ref={ref} style={{ height: HEIGHT }}>
      {width > 0 && (
        <svg width={width} height={HEIGHT} role="img" aria-label={`Team ${metric} over time`}>
          <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
            {yTicks.map(t => (
              <g key={t} transform={`translate(0,${y(t)})`}>
                <line className="hv-grid" x2={innerW} />
                <text className="hv-axis-label" x={-10} dy="0.32em" textAnchor="end">
                  {metric === 'rank' ? `#${t}` : t.toFixed(1)}
                </text>
              </g>
            ))}
            {x.ticks(Math.max(2, Math.floor(innerW / 110))).map(t => (
              <text key={+t} className="hv-axis-label" x={x(t)} y={innerH + 20} textAnchor="middle">
                {tickFormat(t)}
              </text>
            ))}

            {pinnedDate && league.dateIndex.has(pinnedDate) && pinnedDate >= dates[0] && (
              <g transform={`translate(${x(parseDay(pinnedDate))},0)`}>
                <line className="hv-pin-line" y1={-8} y2={innerH} />
                <foreignObject x={-44} y={-30} width={88} height={22}>
                  <div className="hv-pin-label">{formatDay(pinnedDate)}</div>
                </foreignObject>
              </g>
            )}
            {hover && <line className="hv-hover-line" x1={hover.x} x2={hover.x} y2={innerH} />}

            <defs>
              <clipPath id="hv-league-reveal">
                <motion.rect
                  key={dates[0]}
                  x={-8}
                  y={-8}
                  height={innerH + 16}
                  initial={{ width: 0 }}
                  animate={{ width: innerW + 16 }}
                  transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                />
              </clipPath>
            </defs>
            <g clipPath="url(#hv-league-reveal)">
              {order.map(t => {
                const isFocus = t.id === focusId
                const isHover = t.id === hoverId
                const strong = isFocus || isHover
                return (
                  <g key={`${t.id}-${dates[0]}`} style={{ '--line': isFocus ? t.accent : 'var(--hv-ink)' }}>
                    <motion.path
                      className={`hv-line ${strong ? 'is-strong' : ''} ${emphasized && !strong ? 'is-dim' : ''}`}
                      initial={false}
                      animate={{ d: paths.get(t.id) }}
                      transition={{ d: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }}
                    />
                  </g>
                )
              })}
            </g>

            {focus &&
              dates.slice(1).map(d => {
                const v = focus.series[league.dateIndex.get(d)]
                if (!v?.point?.events.length) return null
                return (
                  <circle
                    key={d}
                    className="hv-event-dot"
                    cx={x(parseDay(d))}
                    cy={y(v[metric])}
                    r={d === pinnedDate ? 6 : 4}
                    style={{ fill: focus.accent }}
                  />
                )
              })}

            {hover && tooltipValue && (
              <circle className="hv-hover-dot" cx={hover.x} cy={y(tooltipValue[metric])} r={5} />
            )}

            {league.teams.map(t => {
              const v = t.series[league.dateIndex.get(lastDate)]
              if (!v) return null
              const strong = t.id === focusId || t.id === hoverId
              if (metric === 'score' && !strong) return null
              const size = strong ? 22 : 16
              return (
                <foreignObject
                  key={t.id}
                  x={innerW + 8}
                  y={y(v[metric]) - size / 2}
                  width={size + 4}
                  height={size}
                  className={`hv-end-logo ${emphasized && !strong ? 'is-dim' : ''}`}
                >
                  <button
                    type="button"
                    className="hv-end-logo-btn"
                    title={t.name}
                    onClick={() => onPick({ teamId: t.id, date: null })}
                    onMouseEnter={() => onHover(t.id)}
                    onMouseLeave={() => onHover(null)}
                  >
                    <TeamLogo team={t} size={size} />
                  </button>
                </foreignObject>
              )
            })}

            <rect
              className="hv-hit"
              width={innerW}
              height={innerH}
              onPointerMove={handleMove}
              onPointerLeave={handleLeave}
              onClick={handleClick}
            />
          </g>
        </svg>
      )}

      {hover && (
        <div
          className="hv-tooltip"
          style={{
            left: Math.min(MARGIN.left + hover.x + 16, width - 280),
            top: Math.max(8, Math.min(MARGIN.top + hover.y - 20, HEIGHT - 190)),
          }}
        >
          <div className="hv-tooltip-date">{formatDay(hover.date)}</div>
          {tooltipTeam && tooltipValue ? (
            <>
              <div className="hv-tooltip-team">
                <TeamLogo team={tooltipTeam} size={22} />
                <span className="hv-tooltip-name">{tooltipTeam.name}</span>
              </div>
              <div className="hv-tooltip-stats">
                <span>
                  <b>#{tooltipValue.rank}</b> {tooltipChange && <RankDelta value={tooltipChange.rankDelta} size="sm" />}
                </span>
                <span className="hv-muted">Score {formatScore(tooltipValue.score)}</span>
              </div>
              {tooltipChange?.events.slice(0, 3).map((e, i) => {
                const { verb } = describeEvent(e, league)
                return (
                  <div key={i} className="hv-tooltip-event">
                    <span className={`hv-dot hv-dot--${e.kind}`} />
                    {verb} {e.kind !== 'note' && e.name}
                  </div>
                )
              })}
              {tooltipChange?.events.length > 3 && <div className="hv-tooltip-more">+{tooltipChange.events.length - 3} more</div>}
              {tooltipChange && !tooltipChange.events.length && tooltipChange.rankDelta !== 0 && Math.abs(tooltipChange.scoreDelta) < 1e-6 && (
                <div className="hv-tooltip-event hv-muted">
                  {tooltipChange.rankDelta > 0 ? 'Climbed as other teams dropped' : 'Passed by other teams'}
                </div>
              )}
              <div className="hv-tooltip-hint">Click to explore</div>
            </>
          ) : (
            <div className="hv-muted hv-small">Hover a line to inspect a team</div>
          )}
        </div>
      )}
    </div>
  )
}
